// Shared avatar picker: a pageable grid of deterministic liquid-mercury discs,
// extracted from the inline picker in AccountSettingsScreen so onboarding and
// settings pick avatars through one component. The disc is f(userId, index):
// candidateSeed(userId, i) fed to MercuryAvatar — the ONLY draw path (the
// frozen mercury-avatar-v1 renderer). There is no second renderer here; this
// component only lays out candidates and reports the chosen index.
//
// Nine candidates over three pages of three; "Show more" cycles pages
// (wrapping). Fully keyboard-navigable: arrow keys rove the selection across
// all nine (paging follows), Enter/Space chooses the focused disc.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { useT } from '../../i18n/index.js';

export interface AvatarPickerProps {
  /** Seeds each candidate: candidateSeed(userId, index). */
  userId: string;
  /** Currently-chosen candidate index (0..8), if any. */
  value?: number;
  /** Fires with the chosen candidate index when a disc is picked. */
  onChange: (index: number) => void;
  /** Rendered CSS size of each disc in px (square). Default 72. */
  size?: number;
}

const COUNT = 9; // candidateSeed wraps at 9 (avatar/mercury.ts)
const PER_PAGE = 3;
const PAGES = COUNT / PER_PAGE;

export function AvatarPicker({ userId, value, onChange, size = 72 }: AvatarPickerProps): ReactNode {
  const t = useT();
  // Start on the page holding the current selection (if any) so it's visible.
  const [page, setPage] = useState(() =>
    value !== undefined ? Math.floor((value % COUNT) / PER_PAGE) : 0,
  );
  // Roving-tabindex target; always kept on the visible page so exactly one
  // disc is tabbable. Paging moves it to the page's first disc.
  const [focused, setFocused] = useState(() => (value !== undefined ? value % COUNT : 0));
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // Focus follows keyboard roving only — never steals focus on a pointer page
  // change or the initial mount.
  const pendingFocus = useRef(false);

  useEffect(() => {
    if (!pendingFocus.current) return;
    pendingFocus.current = false;
    refs.current[focused]?.focus();
  }, [focused]);

  const start = page * PER_PAGE;
  const indices = [start, start + 1, start + 2];

  function showMore(): void {
    const next = (page + 1) % PAGES;
    setPage(next);
    setFocused(next * PER_PAGE);
  }

  function roveTo(index: number): void {
    const wrapped = (index + COUNT) % COUNT;
    setFocused(wrapped);
    setPage(Math.floor(wrapped / PER_PAGE));
    pendingFocus.current = true;
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      roveTo(focused + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      roveTo(focused - 1);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        role="radiogroup"
        aria-label={t('avatarPicker.label')}
        onKeyDown={onKeyDown}
        className="grid grid-cols-3 gap-3"
      >
        {indices.map((index) => {
          const selected = value === index;
          return (
            <button
              key={index}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={t('avatarPicker.option', { n: index + 1 })}
              tabIndex={index === focused ? 0 : -1}
              onClick={() => onChange(index)}
              className={`flex items-center justify-center rounded-xl p-2 ${
                selected ? 'bg-raised ring-2 ring-accent' : 'bg-raised'
              }`}
            >
              <MercuryAvatar
                seed={candidateSeed(userId, index)}
                size={size}
                className="rounded-full"
              />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={showMore}
        className="min-h-touch self-start text-sm text-muted underline"
      >
        {t('avatarPicker.showMore')}
      </button>
    </div>
  );
}
